import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ChevronRight, Pencil } from "lucide-react"

import { BotonComentario } from "@/components/Comentarios"
import { LeyendaDelCorte } from "@/components/CorteReal"
import { refCuenta } from "@/routes/comentarios/api"
import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { useRbac } from "@/lib/auth"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount, formatPercent } from "@/lib/format"
import { agrupar, bucketsDe, ultimoBucketReal } from "@/lib/periodo"
import type { Granularidad } from "@/lib/periodo"
import { cn } from "@/lib/utils"

import { fetchDetalleDeLinea, fetchPnl, fetchQuienesDeLaLinea } from "@/routes/finance/api"
import type { QuienDeLaLinea, QuienesDeLaLinea } from "@/routes/finance/api"
import { saveClient, savePerson, saveSupplier } from "@/routes/masters/api"
import { useToast } from "@/components/ui/use-toast"
import { onWriteError } from "@/lib/writeErrors"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { EditarCeldaDialog } from "@/routes/finance/EditarCeldaDialog"
import type { CeldaEditable } from "@/routes/finance/EditarCeldaDialog"
import { useFinanceFilters } from "@/routes/finance/context"
import { nombreCuenta, nombrePais, serieMensual } from "@/routes/finance/mapping"
import { t } from "@/lib/i18n"

interface Props {
  /** Cuenta del modelo, tal como la nombra la hoja. */
  account: string
  titulo: string
  descripcion: string
  /** Nota al pie: supuestos y huecos conocidos de ESTA vista. */
  footnote?: string
}

/**
 * Desglose de UNA cuenta del modelo: sus lineas, periodo a periodo.
 *
 * Revenue, costo de ventas, gastos y payroll son la misma pantalla sobre una
 * cuenta distinta, asi que son un componente y no cuatro archivos casi iguales.
 * Cuatro copias divergen: alguien arregla el orden en una y las otras tres se
 * quedan como estaban.
 *
 * LA COLUMNA "% DE INGRESOS" ES EL PUNTO. Un gasto de 40.000 no dice nada solo;
 * 40.000 sobre ingresos de 60.000 dice bastante. Por eso cada linea se compara
 * contra los ingresos del MISMO periodo y pais, que es lo que permite ver que
 * una linea crece mas rapido que el negocio.
 */
export function DesglosePage({ account, titulo, descripcion, footnote }: Props) {
  const { activeTenantId } = useAuth()
  const { can } = useRbac()
  const { anio, pais, paisQuery, listo, granularidad } = useFinanceFilters()
  const [celda, setCelda] = React.useState<CeldaEditable | null>(null)
  /*
    QUÉ LÍNEAS ESTÁN ABIERTAS. Un `Set` y no una sola línea: comparar «B2B Lead
    Gen» con «Staffing» por país es justo para lo que se abre, y dejar sólo una
    abierta obligaría a cerrar la primera para ver la segunda.
  */
  const [abiertas, setAbiertas] = React.useState<Set<string>>(() => new Set())
  const alternar = React.useCallback((sub: string) => {
    setAbiertas((previas) => {
      const siguiente = new Set(previas)
      if (siguiente.has(sub)) siguiente.delete(sub)
      else siguiente.add(sub)
      return siguiente
    })
  }, [])
  /*
    POR QUÉ EL LÁPIZ «NO FUNCIONABA», Y NO ERA UN FALLO DE CÓDIGO.

    Cesar: «el lápiz de corregir un importe no funciona, tal vez sólo con que se
    edite la hoja está bien».

    El botón estaba `disabled` cuando no hay sociedad elegida o la vista no es
    mensual — con buen motivo: un importe se guarda por (país, línea, año, MES),
    y en trimestral o consolidado lo que se ve es la suma de varias celdas, que
    no se puede repartir sin inventar un criterio.

    El problema era el SILENCIO. Pulsabas un número y no pasaba absolutamente
    nada: ni se abría el diálogo, ni aparecía un motivo. La explicación existía,
    pero en una nota gris de 11 px encima de la tabla, que nadie relaciona con
    el clic que acaba de no hacer nada. Un mando que no responde se lee como
    roto, aunque esté desactivado a conciencia.

    Ahora el clic SIEMPRE contesta: o abre el diálogo, o dice qué falta para
    poder abrirlo, donde se está mirando.
  */
  const [avisoEdicion, setAvisoEdicion] = React.useState<string | null>(null)

  /*
    SOLO SE EDITA LO QUE ES UNA CELDA.

    Un importe se guarda por (país, línea, año, MES). En vista trimestral o
    consolidada, lo que se ve en pantalla es la suma de varias celdas: no hay
    forma de repartir un cambio entre ellas sin inventar el criterio. Antes que
    adivinar, la edición se habilita solo con un país elegido y granularidad
    mensual, y se dice por qué está apagada.
  */
  const puedeEditar = can("edit", activeTenantId ?? undefined)
  const editable = puedeEditar && Boolean(pais) && granularidad === "mes"
  const porQueNo = !puedeEditar
    ? "Tu rol no puede editar en este tenant."
    : !pais
      ? "Elige una sociedad arriba: sin ella no se sabe a qué celda va la corrección."
      : "Cambia el período a mensual: un importe se guarda por mes."

  const pnl = useQuery({
    queryKey: ["finance-pnl", activeTenantId, anio, pais],
    queryFn: () => fetchPnl(anio, paisQuery),
    // `listo`: con una sociedad elegida, o en Holding siempre. Ver `context`.
    enabled: Boolean(activeTenantId) && listo,
  })

  /*
    CON LA FRONTERA DECLARADA HAY CELDAS QUE NO SE PUEDEN CORREGIR AQUÍ, y se
    dice por qué en vez de abrir un diálogo que no va a cambiar nada:

    * un mes ARMADO ya no lee la hoja —sale del libro y del maestro—, así que
      corregir la hoja no movería la cifra (el backend lo rechaza con un 409);
    * una fila del PUENTE («Ingresos recurrentes (MRR)») junta varias líneas de
      tu hoja, y una corrección no se puede repartir entre ellas sin inventar
      el criterio.
  */
  const armado = pnl.data?.ensamblado ?? null
  const noEditable = (sub: string, meses: number[]): string | null => {
    if (!armado) return null
    if (meses.some((m) => armado.meses_ensamblados.includes(m))) {
      return t("Este mes ya no sale de tu hoja: sale de tu libro y tu maestro. Se corrige en el asiento o en el contrato.")
    }
    const juntas = armado.filas_del_puente[sub]
    if (juntas) {
      return `${t("Esta fila junta varias líneas de tu hoja")} (${juntas.join(", ")}). ${t("Una corrección no se puede repartir entre ellas: corrígela en tu hoja.")}`
    }
    return null
  }

  const cuenta = pnl.data?.cuentas.find((c) => c.account === account)
  const ingresos = pnl.data?.cuentas.find((c) => c.account === "Revenue")
  const buckets = bucketsDe(granularidad, anio)

  /*
    LA LINEA ROJA, TAMBIEN AQUI. Cesar: «la linea roja de separacion de real vs
    forecast replicalo en todo, tambien en revenue, costo de ventas, gastos,
    payroll, etc.».

    No hizo falta ni un endpoint ni un campo: `/pnl` YA devuelve `corte` en todas
    sus respuestas —se puso asi a proposito, para que la pantalla no tenga que
    saber que origen esta mirando— y esta pantalla lo estaba tirando. Revenue,
    costo de ventas, gastos y payroll son las cuatro el mismo componente, asi
    que pintarlo una vez las cubre las cuatro.

    `corte.mes` es un MES y la tabla puede ir por trimestres: un trimestre con un
    mes sin respaldo cae del lado del forecast. Eso lo resuelve
    `ultimoBucketReal`, que es la misma regla que usan P&L y caja — y es la razon
    de que viva en `lib/periodo.ts` y no en cada pantalla.
  */
  const corte = pnl.data?.corte
  const bucketReal = ultimoBucketReal(buckets, corte?.mes)

  const serieIngresos = agrupar(serieMensual(ingresos?.meses), granularidad, anio)
  const serieCuenta = agrupar(serieMensual(cuenta?.meses), granularidad, anio)
  const totalCuenta = cuenta?.total ?? 0
  const totalIngresos = ingresos?.total ?? 0

  // Las lineas, de mayor a menor: lo que mas pesa se lee primero.
  const lineas = [...(cuenta?.lineas ?? [])].sort((a, b) => b.total - a.total)

  return (
    <div className="space-y-4">
      <MetricRow className="lg:grid-cols-3">
        <MetricCard label={`${titulo} · ${anio}`} value={totalCuenta} loading={pnl.isLoading} />
        <MetricCard
          label={t("Sobre ventas")}
          value={null}
          loading={pnl.isLoading}
          badge={totalIngresos
            ? formatPercent(totalCuenta / totalIngresos, 1)
            : "sin ingresos con que comparar"}
        />
        <MetricCard label={t("Líneas que lo componen")} value={null} loading={pnl.isLoading}
                    badge={String(lineas.length)} />
      </MetricRow>

      {puedeEditar ? (
        <p className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <Pencil className="h-3 w-3" />
          {editable
            ? t("Haz clic en un importe para corregirlo. La hoja no se toca: la corrección se guarda aparte y manda sobre ella.")
            : `${t("Edición no disponible en esta vista.")} ${porQueNo}`}
        </p>
      ) : null}

      {/*
        LA RESPUESTA AL CLIC QUE NO PUDO ABRIR NADA. En ámbar y con el motivo,
        porque lo que falta es una acción de quien lee —elegir sociedad, o pasar
        a mensual— y no un fallo del sistema.
      */}
      {avisoEdicion ? (
        <p
          role="status"
          className="flex items-start gap-1.5 rounded-md border border-warning/35 bg-warning-soft p-2.5 text-2xs leading-4"
        >
          <Pencil className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
          {avisoEdicion}
        </p>
      ) : null}

      <WorkPanel
        title={titulo}
        meta={<span className="text-2xs text-muted-foreground">{descripcion}</span>}
        footnote={footnote}
      >
        <DataState
          loading={pnl.isLoading}
          error={pnl.error}
          empty={Boolean(pnl.data) && lineas.length === 0}
          emptyMessage={`No hay ${titulo.toLowerCase()} cargado para ${anio}.`}
          emptyHint={t("Tu hoja de gestión aún no está cargada para este año. Se carga en Primeros pasos, en «Tu facturación».")}
          onRetry={() => void pnl.refetch()}
          rows={8}
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[20rem]">{t("Línea")}</TableHead>
                  {buckets.map((b, i) => (
                    <TableHead
                      key={b.label}
                      title={b.nombre}
                      className={cn("w-24 text-right", i === bucketReal && "corte-real")}
                    >
                      {b.label}
                    </TableHead>
                  ))}
                  <TableHead className="w-28 text-right">{t("Total")}</TableHead>
                  <TableHead className="w-24 text-right">{t("% del total")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lineas.map((linea) => {
                  const serie = agrupar(serieMensual(linea.meses), granularidad, anio)
                  const abierta = abiertas.has(linea.sub_account)
                  return (
                    <React.Fragment key={linea.sub_account}>
                    <TableRow>
                      {/*
                        CADA LÍNEA SE ABRE, HAYA O NO SOCIEDAD ELEGIDA.

                        Se construyó abriéndose sólo en el consolidado, con el
                        argumento de que con un país ya elegido el desglose daría
                        una sola fila. El argumento era cierto y la conclusión
                        estaba mal: Cesar entró con Perú puesto y **no había nada
                        que pulsar**, así que lo que vio fue que la función no
                        existe. «Sigo sin poder ver el detalle», literal.

                        Un mando que desaparece según el filtro es indistinguible
                        de un mando que no se construyó. Ahora se abre siempre, y
                        cuando el desglose es una sola fila el propio detalle lo
                        dice — que es información, no una promesa incumplida.
                      */}
                      <TableCell>
                        <span className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => alternar(linea.sub_account)}
                            aria-expanded={abierta}
                            className="flex min-w-0 items-center gap-1 text-left hover:text-brand"
                          >
                            <ChevronRight
                              className={cn("h-3 w-3 shrink-0 transition-transform",
                                            abierta && "rotate-90")}
                              aria-hidden="true"
                            />
                            {linea.sub_account}
                          </button>
                          <BotonComentario ancla={{ ambito: "cifra", referencia: refCuenta(`${account}/${linea.sub_account}`, anio),
                                                    rotulo: `${linea.sub_account} · ${anio}`, anio }} />
                        </span>
                      </TableCell>
                      {serie.map((b, i) => (
                        <TableCell
                          key={b.label}
                          className={cn("num p-0", i === bucketReal && "corte-real")}
                        >
                          <button
                            type="button"
                            /*
                              NO VA `disabled`, y es el arreglo. Un botón
                              deshabilitado no recibe el clic, así que no puede
                              explicar nada: el usuario pulsa, no pasa nada, y
                              concluye que está roto.
                            */
                            title={noEditable(linea.sub_account, b.meses)
                              ?? (editable
                                ? "Corregir este importe"
                                : `No se puede editar aquí. ${porQueNo}`)}
                            onClick={() => {
                              const bloqueo = noEditable(linea.sub_account, b.meses)
                              if (bloqueo) {
                                setAvisoEdicion(bloqueo)
                                return
                              }
                              if (!editable || !pais) {
                                setAvisoEdicion(porQueNo)
                                return
                              }
                              setAvisoEdicion(null)
                              setCelda({
                                pais, bloque: "income_statement", account,
                                sub_account: linea.sub_account,
                                anio, mes: b.meses[0], importe_usd: b.valor,
                              })
                            }}
                            className={cn(
                              "num h-full w-full px-2 py-1.5 text-right",
                              editable && "rounded-[3px] hover:bg-row-hover",
                              editable && "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              !editable && "cursor-default",
                            )}
                          >
                            {b.valor === 0 ? "" : formatAmount(b.valor)}
                          </button>
                        </TableCell>
                      ))}
                      <TableCell className="num font-medium">{formatAmount(linea.total)}</TableCell>
                      <TableCell className="num text-muted-foreground">
                        {totalCuenta ? formatPercent(linea.total / totalCuenta, 1) : ""}
                      </TableCell>
                    </TableRow>
                    {abierta ? (
                      <DetalleDeLaLinea
                        anio={anio}
                        account={account}
                        subAccount={linea.sub_account}
                        pais={paisQuery}
                        granularidad={granularidad}
                        buckets={buckets.length}
                        bucketReal={bucketReal}
                      />
                    ) : null}
                    </React.Fragment>
                  )
                })}

                <TableRow className="bg-surface-sunken font-semibold">
                  <TableCell>{nombreCuenta(account)}</TableCell>
                  {serieCuenta.map((b, i) => (
                    <TableCell
                      key={b.label}
                      className={cn("num", i === bucketReal && "corte-real")}
                    >
                      {formatAmount(b.valor)}
                    </TableCell>
                  ))}
                  <TableCell className="num">{formatAmount(totalCuenta)}</TableCell>
                  <TableCell className="num">{totalCuenta ? "100.0 %" : ""}</TableCell>
                </TableRow>

                {/*
                  La fila que da contexto. Sin ella, un costo que sube se lee
                  como un problema aunque el negocio haya crecido mas.
                */}
                {account !== "Revenue" ? (
                  <TableRow className="text-2xs text-muted-foreground">
                    <TableCell>{t("Sobre ventas del período")}</TableCell>
                    {serieCuenta.map((b, i) => {
                      const ing = serieIngresos[i]?.valor ?? 0
                      return (
                        <TableCell
                          key={b.label}
                          className={cn("num", i === bucketReal && "corte-real")}
                        >
                          {ing ? formatPercent(b.valor / ing, 1) : ""}
                        </TableCell>
                      )
                    })}
                    <TableCell className="num">
                      {totalIngresos ? formatPercent(totalCuenta / totalIngresos, 1) : ""}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
          <LeyendaDelCorte corte={corte} bucketReal={bucketReal} className="px-2.5 pb-2 pt-1" />
        </DataState>
      </WorkPanel>

      <EditarCeldaDialog
        celda={celda}
        open={Boolean(celda)}
        onOpenChange={(v) => { if (!v) setCelda(null) }}
      />
    </div>
  )
}


/**
 * UNA LÍNEA ABIERTA: sus países, mes a mes.
 *
 * Se pide SÓLO al abrirla y no con la tabla: son cinco líneas y abrirlas todas
 * de antemano serían cinco consultas que casi nadie va a mirar. `useQuery` con
 * la línea en la clave además las cachea por separado, así que cerrar y volver
 * a abrir no vuelve a pedir nada.
 */
function DetalleDeLaLinea({
  anio, account, subAccount, pais, granularidad, buckets, bucketReal,
}: {
  anio: number
  account: string
  subAccount: string
  /** La sociedad elegida arriba, si hay. Acota el desglose a su país. */
  pais?: string
  granularidad: Granularidad
  buckets: number
  /** `null` cuando no hay corte declarado: entonces no se raya ninguna columna. */
  bucketReal: number | null
}) {
  const { activeTenantId } = useAuth()
  const q = useQuery({
    queryKey: ["finance-linea", activeTenantId, anio, account, subAccount, pais],
    queryFn: () => fetchDetalleDeLinea(anio, account, subAccount, pais),
    enabled: Boolean(activeTenantId),
  })
  /*
    POR QUIÉN. Cesar, 2026-10-05: «si le doy click a B2B Lead Gen sigue
    apareciendo así. Debería de aparecer una lista con cada cliente con su
    monto en cada mes de venta, lo mismo para costo de ventas, gastos, costo de
    equipo». La hoja no lo sabe; el maestro sí (migración 0038). Ver
    `quienes.py` y `QuienesDeLaLinea` abajo.
  */
  const quienes = useQuery({
    queryKey: ["finance-linea-quienes", activeTenantId, anio, account, subAccount, pais],
    queryFn: () => fetchQuienesDeLaLinea(anio, account, subAccount, pais),
    enabled: Boolean(activeTenantId),
  })

  //: +3: la columna de la línea, la del total y la del porcentaje.
  const columnas = buckets + 3

  if (q.isLoading || quienes.isLoading) {
    return (
      <TableRow>
        <TableCell colSpan={columnas} className="py-1.5 pl-7 text-2xs text-muted-foreground">
          {t("Abriendo la línea…")}
        </TableCell>
      </TableRow>
    )
  }

  const paises = q.data?.paises ?? []
  const porQuien = quienes.data ? (
    <QuienesDeLaLinea d={quienes.data} granularidad={granularidad} anio={anio}
                      bucketReal={bucketReal} columnas={columnas} />
  ) : null

  if (paises.length === 0) {
    return (
      <>
        {porQuien}
        <TableRow>
          <TableCell colSpan={columnas} className="py-1.5 pl-7 text-2xs leading-5 text-muted-foreground">
            {t("Esta línea no tiene importes en la hoja en esta vista.")}
          </TableCell>
        </TableRow>
      </>
    )
  }

  /*
    CON UNA SOLA FILA, EL DESGLOSE NO AÑADE NADA Y SE DICE.

    Pasa cuando hay una sociedad elegida: el país es uno y la fila repite la de
    arriba. Repetirla sin más haría pensar que el detalle se quedó a medias; lo
    que de verdad falta —los clientes— no está en la hoja, y decir dónde SÍ está
    es la única respuesta útil.
  */
  if (paises.length === 1) {
    return porQuien
  }

  return (
    <>
      {porQuien}
      {paises.map((p) => {
        const serie = agrupar(serieMensual(p.meses), granularidad, anio)
        return (
          <TableRow key={p.pais} className="bg-surface-sunken/40">
            <TableCell className="pl-7 text-xs text-muted-foreground">
              {nombrePais(p.pais)}
            </TableCell>
            {serie.map((b, i) => (
              <TableCell
                key={b.label}
                className={cn("num text-muted-foreground", i === bucketReal && "corte-real")}
              >
                {b.valor === 0 ? "" : formatAmount(b.valor)}
              </TableCell>
            ))}
            <TableCell className="num text-muted-foreground">{formatAmount(p.total)}</TableCell>
            <TableCell />
          </TableRow>
        )
      })}
    </>
  )
}

const SIN_LINEA = "__sin_linea__"
const ROTULO: Record<QuienesDeLaLinea["tipo"], string> = {
  cliente: "Por cliente", proveedor: "Por proveedor", persona: "Por persona",
}

/**
 * LA LÍNEA, POR QUIÉN. Cada cliente / proveedor / persona de la línea con su
 * mes a mes; después la DIFERENCIA contra la hoja (lo que la hoja dice y
 * nadie explica), y al final lo que tiene dinero pero NO tiene línea, con un
 * desplegable para asignársela ahí mismo —que es donde uno se da cuenta de
 * que falta—. Asignar escribe en el maestro con motivo y la línea se vuelve a
 * leer.
 */
function QuienesDeLaLinea({ d, granularidad, anio, bucketReal, columnas }: {
  d: QuienesDeLaLinea; granularidad: Granularidad; anio: number
  bucketReal: number | null; columnas: number
}) {
  const { can } = useRbac()
  const { toast } = useToast()
  const qc = useQueryClient()
  const puedeEditar = can("edit")
  const asignar = useMutation({
    mutationFn: async (x: { quien: QuienDeLaLinea; linea: string }) => {
      const reason = `Línea de la hoja asignada desde el desglose: ${x.linea}.`
      if (!x.quien.id) {
        // UN CLIENTE QUE FACTURA Y NO ESTÁ EN EL MAESTRO SE CREA DESDE AQUÍ,
        // con lo que la factura sabe de él (identificador fiscal, país,
        // moneda) y la línea puesta. Mandar a otra pantalla a crearlo para
        // volver aquí a asignarle la línea es el tipo de vuelta que nadie da.
        if (d.tipo !== "cliente") throw new Error("No está en el maestro.")
        return saveClient({
          data: {
            name: x.quien.nombre, ruc: x.quien.tax_id || `SIN-RUC-${x.quien.nombre.slice(0, 12)}`,
            country: x.quien.pais || "PE", currency: x.quien.moneda || "USD",
            paymentTermsDays: 30, lineaHoja: x.linea,
          },
          reason: `Creado desde el desglose a partir de sus facturas, con línea ${x.linea}.`,
        })
      }
      if (d.tipo === "cliente") return saveClient({ data: { id: x.quien.id, lineaHoja: x.linea }, reason })
      if (d.tipo === "proveedor") return saveSupplier({ data: { id: x.quien.id, lineaHoja: x.linea }, reason })
      return savePerson({ data: { id: x.quien.id, lineaHoja: x.linea }, reason })
    },
    onSuccess: (_r, x) => {
      toast({ title: `${x.quien.nombre} → ${x.linea}`,
              description: x.quien.id ? undefined : t("Creado en Maestros › Clientes con lo que decían sus facturas.") })
      void qc.invalidateQueries({ queryKey: ["finance-linea-quienes"] })
      void qc.invalidateQueries({ queryKey: [d.tipo === "cliente" ? "clients" : d.tipo === "proveedor" ? "suppliers" : "people"] })
    },
    onError: onWriteError("la línea"),
  })

  const celdas = (meses: Record<string, number>, apagado = false) =>
    agrupar(serieMensual(meses), granularidad, anio).map((b, i) => (
      <TableCell key={b.label}
                 className={cn("num", apagado ? "text-muted-foreground" : "", i === bucketReal && "corte-real")}>
        {b.valor === 0 ? "" : formatAmount(b.valor)}
      </TableCell>
    ))
  const total = (meses: Record<string, number>) =>
    Object.values(meses).reduce((s, v) => s + v, 0)
  const hayDiferencia = Object.values(d.diferencia).some((v) => Math.abs(v) >= 0.5)

  return (
    <>
      {d.filas.length === 0 && d.sin_linea.length === 0 ? (
        <TableRow className="bg-surface-sunken/40">
          <TableCell colSpan={columnas} className="py-1.5 pl-7 text-2xs leading-5 text-muted-foreground">
            {d.tipo === "cliente"
              ? t("Ningún cliente tiene esta línea en el maestro ni hay facturas sin línea este año.")
              : d.tipo === "proveedor"
                ? t("Ningún proveedor tiene esta línea en el maestro ni hay comprobantes sin línea este año.")
                : t("Ninguna persona cae en esta línea, ni por elección ni por régimen.")}
          </TableCell>
        </TableRow>
      ) : null}
      {d.filas.map((f) => (
        <TableRow key={f.id ?? f.nombre} className="bg-surface-sunken/40">
          <TableCell className="pl-7 text-xs">
            {f.nombre}
            {f.por_defecto ? <span className="ml-1.5 text-2xs text-muted-foreground">{t("por régimen")}</span> : null}
          </TableCell>
          {celdas(f.meses)}
          <TableCell className="num">{formatAmount(f.total)}</TableCell>
          <TableCell className="num text-muted-foreground">
            {total(d.hoja) ? formatPercent(f.total / total(d.hoja), 1) : ""}
          </TableCell>
        </TableRow>
      ))}
      {d.filas.length > 0 && hayDiferencia ? (
        <TableRow className="bg-surface-sunken/40">
          <TableCell className="pl-7 text-2xs text-muted-foreground" title={t("Lo que la hoja dice y ninguno de los de arriba explica. Positivo: la hoja trae más; negativo: el maestro suma más que la hoja.")}>
            {t("Diferencia contra la hoja")}
          </TableCell>
          {celdas(d.diferencia, true)}
          <TableCell className="num text-muted-foreground">{formatAmount(total(d.diferencia))}</TableCell>
          <TableCell />
        </TableRow>
      ) : null}
      {d.sin_linea.map((f) => (
        <TableRow key={`sin-${f.id ?? f.nombre}`} className="bg-warning-soft/30">
          <TableCell className="pl-7 text-xs">
            <span className="flex flex-wrap items-center gap-1.5">
              <span>{f.nombre}</span>
              {puedeEditar && (f.id || d.tipo === "cliente") ? (
                <Select onValueChange={(v) => { if (v !== SIN_LINEA) asignar.mutate({ quien: f, linea: v }) }}>
                  <SelectTrigger className="h-6 w-52 text-2xs" aria-label={t("Asignar línea")}>
                    <SelectValue placeholder={f.id ? t("Sin línea · asignar") : t("No está en el maestro · crear con línea")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SIN_LINEA} disabled>{f.id ? t("Sin línea · asignar") : t("Crear el cliente con la línea…")}</SelectItem>
                    {d.lineas.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-2xs text-muted-foreground">
                  {f.id ? t("sin línea") : t("sin línea · no está en el maestro con ese nombre")}
                </span>
              )}
            </span>
          </TableCell>
          {celdas(f.meses, true)}
          <TableCell className="num text-muted-foreground">{formatAmount(f.total)}</TableCell>
          <TableCell />
        </TableRow>
      ))}
      <TableRow className="bg-surface-sunken/40">
        <TableCell colSpan={columnas} className="py-1 pl-7 text-2xs leading-4 text-muted-foreground">
          <span className="font-medium text-foreground">{t(ROTULO[d.tipo])}</span> · {d.fuente}
          {d.sin_cotizacion ? ` · ${d.sin_cotizacion} ${t("sin cotización (no se cuentan)")}` : ""}
        </TableCell>
      </TableRow>
    </>
  )
}
